'use client';

/**
 * The Admin dashboard's list pattern (docs/pdlc/admin-dashboard.md, "Page
 * pattern"): every list page is a toolbar over a table on a white card. One type
 * scale: a row's name is `font-medium` at the table's size (text-small), and
 * everything secondary is text-tiny in foreground-600. A row has at most one
 * visible action, its main one, and the rest in its menu, so no row mixes links,
 * buttons and loose text.
 *
 * - StaffToolbar: search, filters or tabs, and the count on one line.
 * - STAFF_TABLE: the classNames every HeroUI Table on an Admin page takes.
 * - CellStack: a cell's name with one secondary line under it.
 * - RowActions: the main action as a bordered button, the rest in a "…" menu.
 */
import {
    Button,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownTrigger,
    Input,
    Spinner,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import type { ReactNode } from 'react';

/** Pass as `classNames` to every HeroUI `Table` on an Admin page. */
export const STAFF_TABLE = {
    wrapper: 'rounded-large bg-content1 p-0 shadow-small',
    th: 'bg-default-100 text-tiny font-semibold uppercase tracking-wide text-foreground-600',
    td: 'align-top py-3 text-small',
} as const;

export function StaffToolbar({
    search,
    filters,
    count,
    loading = false,
    end,
}: {
    search?: {
        value: string;
        onChange: (v: string) => void;
        placeholder: string;
        /** A page that is a search (Search, Projects, Panelists) focuses it on arrival. */
        autoFocus?: boolean;
    };
    /** Tabs, selects or switches that narrow the list. */
    filters?: ReactNode;
    /** "2,338 surveys". */
    count?: ReactNode;
    loading?: boolean;
    /** Buttons that belong to the whole list (Export, New ...). */
    end?: ReactNode;
}) {
    return (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            {search ? (
                <Input
                    className="w-full max-w-sm"
                    size="sm"
                    type="search"
                    aria-label={search.placeholder}
                    placeholder={search.placeholder}
                    startContent={
                        <Icon
                            icon="lucide:search"
                            className="h-4 w-4 text-default-500"
                            aria-hidden
                        />
                    }
                    value={search.value}
                    onValueChange={search.onChange}
                    autoFocus={search.autoFocus}
                    isClearable
                    onClear={() => search.onChange('')}
                />
            ) : null}
            {filters}
            <span className="ml-auto flex items-center gap-3 text-small text-foreground-600">
                {loading ? <Spinner size="sm" aria-label="Loading" /> : null}
                {count}
                {end}
            </span>
        </div>
    );
}

export function CellStack({
    primary,
    secondary,
    title,
}: {
    primary: ReactNode;
    secondary?: ReactNode;
    /** The full name, when `primary` may be clamped. */
    title?: string;
}) {
    return (
        <div className="flex min-w-0 flex-col gap-0.5">
            <span className="line-clamp-2 font-medium text-foreground" title={title}>
                {primary}
            </span>
            {secondary ? (
                <span className="truncate text-tiny text-foreground-600">{secondary}</span>
            ) : null}
        </div>
    );
}

export interface RowMenuItem {
    key: string;
    label: string;
    icon: string;
    href?: string;
    onPress?: () => void;
    /** Destructive: shown last, in red. */
    danger?: boolean;
    disabled?: boolean;
}

export function RowActions({
    label,
    primary,
    menu = [],
}: {
    /** What the row is, for the menu's accessible name ("survey 212754"). */
    label: string;
    /** The row's one visible action, a small bordered Button. */
    primary?: ReactNode;
    menu?: RowMenuItem[];
}) {
    const items = [...menu.filter((m) => !m.danger), ...menu.filter((m) => m.danger)];
    return (
        <div className="flex items-center justify-end gap-1">
            {primary}
            {items.length ? (
                <Dropdown placement="bottom-end">
                    <DropdownTrigger>
                        <Button
                            size="sm"
                            variant="light"
                            isIconOnly
                            aria-label={`More for ${label}`}
                        >
                            <Icon icon="lucide:ellipsis" className="h-4 w-4" aria-hidden />
                        </Button>
                    </DropdownTrigger>
                    <DropdownMenu
                        aria-label={label}
                        disabledKeys={items.filter((m) => m.disabled).map((m) => m.key)}
                        onAction={(key) => items.find((m) => m.key === key)?.onPress?.()}
                    >
                        {items.map((m) => (
                            <DropdownItem
                                key={m.key}
                                href={m.href}
                                color={m.danger ? 'danger' : 'default'}
                                className={m.danger ? 'text-danger' : undefined}
                                startContent={
                                    <Icon icon={m.icon} className="h-4 w-4" aria-hidden />
                                }
                            >
                                {m.label}
                            </DropdownItem>
                        ))}
                    </DropdownMenu>
                </Dropdown>
            ) : null}
        </div>
    );
}

/** A small bordered button for a row's one visible action. */
export function RowButton({
    icon,
    children,
    ...props
}: { icon?: string; children: ReactNode } & Omit<
    React.ComponentProps<typeof Button>,
    'size' | 'variant' | 'children'
>) {
    return (
        <Button
            size="sm"
            variant="bordered"
            startContent={
                icon ? <Icon icon={icon} className="h-3.5 w-3.5" aria-hidden /> : undefined
            }
            {...props}
        >
            {children}
        </Button>
    );
}
